import { APIClientServices } from "./APIClientServices";

export type NotificationType =
	| "task_completed"
	| "task_failed"
	| "oauth_required"
	| "rate_limit_hit"
	| "stripe_invoice_paid"
	| "stripe_payment_failed"
	| (string & {});

export type NotificationChannel = "in_app" | "email";

export interface PublicNotificationDTO {
	id: string;
	accountId: string;
	type: NotificationType;
	title: string;
	message: string;
	data: Record<string, any>;
	channelsSent: NotificationChannel[];
	read: boolean;
	readAt: string | null;
	createdAt: string;
}

export type PerTypeOverrides = Partial<Record<NotificationType, Partial<Record<NotificationChannel, boolean>>>>;

export interface PublicNotificationPreferencesDTO {
	accountId: string;
	inAppEnabled: boolean;
	emailEnabled: boolean;
	perTypeOverrides: PerTypeOverrides;
	createdAt: string;
	updatedAt: string;
}

export interface ListNotificationsFilters {
	limit?: number;
	offset?: number;
	unread?: boolean;
	type?: NotificationType;
}

export interface UpdateNotificationPreferencesInput {
	inAppEnabled?: boolean;
	emailEnabled?: boolean;
	perTypeOverrides?: PerTypeOverrides;
}

export class NotificationServices {
	static async list(filters: ListNotificationsFilters = {}): Promise<PublicNotificationDTO[]> {
		// Query params devem ser passados via 2o arg do APIClientServices.get
		// (que os atribui a url.searchParams). Concatenar `?...` no path quebra
		// porque URL.pathname codifica `?` como `%3F` -> backend vê o nome do
		// path como "/notifications?limit=50" e retorna 404.
		const params: Record<string, string | number | boolean | null | undefined> = {};
		if (filters.limit !== undefined) params.limit = filters.limit;
		if (filters.offset !== undefined) params.offset = filters.offset;
		if (filters.unread) params.unread = true;
		if (filters.type) params.type = filters.type;
		return APIClientServices.get<PublicNotificationDTO[]>("v1/notifications", params);
	}

	static async unreadCount(): Promise<number> {
		const { count } = await APIClientServices.get<{ count: number }>("v1/notifications/unread-count");
		return count;
	}

	static async markRead(id: string): Promise<void> {
		await APIClientServices.post(`v1/notifications/${id}/read`, {});
	}

	static async markAllRead(): Promise<number> {
		const { updated } = await APIClientServices.post<{ updated: number }>("v1/notifications/read-all", {});
		return updated;
	}

	static async getPreferences(): Promise<PublicNotificationPreferencesDTO> {
		return APIClientServices.get<PublicNotificationPreferencesDTO>("v1/notifications/preferences");
	}

	static async updatePreferences(
		patch: UpdateNotificationPreferencesInput,
	): Promise<PublicNotificationPreferencesDTO> {
		return APIClientServices.put<PublicNotificationPreferencesDTO>("v1/notifications/preferences", patch);
	}
}
